<script setup lang="ts">
import { trackEvent } from '@@/bits/analytics'
import { isAppCapableOf } from '@@/bits/app_can'
import { isTouchScreen } from '@@/bits/browser'
import { ContentPickerSource } from '@@/bits/content_picker'
import type { HTMLStyleElementWrapper } from '@@/bits/css'
import { appendCssToDocumentHead } from '@@/bits/css'
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { awaitDOMUpdates, awaitTransitionEnd, isHtmlElementAnInputField } from '@@/bits/dom'
import { captureMessage } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { defineAsyncComponent } from '@@/bits/surface_vue'
import { getEditorObject } from '@@/bits/tldraw/editor'
import { sanitizeUrl } from '@@/bits/url'
import type { PageMeta } from '@@/bits/whiteboard'
import {
  ActionType,
  BackgroundType,
  FRAME_ID_QUERY_PARAM,
  TldrawAppRefKey,
  actionShortcuts,
  changeAllTldrawDefaultColorStyleColor,
  handleAction,
  isShapeApproved,
  migrateExistingTextShapesToUseThemeFont,
  shouldEnableGroupingForShapes,
  switchFrame,
} from '@@/bits/whiteboard'
import { initializeLinking, listenToLinkingChanges, triggerLinking } from '@@/bits/whiteboard_linking'
import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  shouldEnableWhiteboardInPlaceUploadingAnimation,
} from '@@/bits/whiteboard_no_dependencies'
import type { TldrawDefaultColorStyleColor } from '@@/bits/whiteboard_tools'
import { getTldrawToolId } from '@@/bits/whiteboard_tools'
import {
  MAXIMUM_NUMBER_OF_FILES,
  calculateUploadedFilePositionsAndDimensions,
  deleteFailedUploads,
} from '@@/bits/whiteboard_upload'
import type { Anchor } from '@@/library/v4/components/OzBasePopover.vue'
import OzBasePopover, { OzBasePopoverColors } from '@@/library/v4/components/OzBasePopover.vue'
import { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzDivider, { OzDividerColorScheme } from '@@/library/v4/components/OzDivider.vue'
import type { OzMenuRowsRow } from '@@/library/v4/components/OzMenuRows.vue'
import OzMenuRows, {
  OzMenuRowsFocusStyle,
  OzMenuRowsRowStyle,
  OzMenuRowsSizePreset,
} from '@@/library/v4/components/OzMenuRows.vue'
import OzPopover from '@@/library/v4/components/OzPopover.vue'
import { openNativeWhiteboardBackgroundPicker } from '@@/native_bridge/actions'
import postMessage from '@@/native_bridge/post_message'
import { usePersonalPlansStore } from '@@/pinia/personal_plans_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfacePanelsStore } from '@@/pinia/surface_panels'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { useSurfaceWindowMobileNotchStore } from '@@/pinia/surface_window_mobile_notch'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { WHITEBOARD_ATTACHMENTS_LOADED_ID, useWhiteboardAttachmentsStore } from '@@/pinia/whiteboard_attachments'
import { useWhiteboardCardTemplatesStore } from '@@/pinia/whiteboard_card_templates'
import { useWhiteboardElementModifierStore } from '@@/pinia/whiteboard_element_modifier'
import { useWhiteboardFrameBackgroundStore } from '@@/pinia/whiteboard_frame_background'
import { useWhiteboardLinkingStore } from '@@/pinia/whiteboard_linking'
import { useWhiteboardModeStore } from '@@/pinia/whiteboard_mode'
import { useWhiteboardObjectPreviewStore } from '@@/pinia/whiteboard_object_preview'
import { useWhiteboardPanelsStore } from '@@/pinia/whiteboard_panels'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import { useWhiteboardScriptStore } from '@@/pinia/whiteboard_script'
import { useWhiteboardSpecialEffectsStore } from '@@/pinia/whiteboard_special_effects'
import { useWhiteboardToolbarStore } from '@@/pinia/whiteboard_toolbar'
import { useWhiteboardToolsStore } from '@@/pinia/whiteboard_tools'
import { useWhiteboardUploadStore } from '@@/pinia/whiteboard_upload'
import { useWhiteboardZoomLevelStore } from '@@/pinia/whiteboard_zoom_level'
import { BreakPoints, useWindowSizeStore } from '@@/pinia/window_size'
import SurfaceWhiteboardModeSwitcher from '@@/vuecomponents//SurfaceWhiteboardModeSwitcher.vue'
import MembershipTierUpgradeModal from '@@/vuecomponents/MembershipTierUpgradeModal.vue'
import SurfaceWhiteboardCanvasDragAndDropOverlay from '@@/vuecomponents/SurfaceWhiteboardCanvasDragAndDropOverlay.vue'
import WhiteboardAttachmentPicker from '@@/vuecomponents/WhiteboardAttachmentPicker.vue'
import WhiteboardCodeFrameObjectPicker from '@@/vuecomponents/WhiteboardCodeFrameObjectPicker.vue'
import WhiteboardFrameBarOld from '@@/vuecomponents/WhiteboardFrameBarOld.vue'
import WhiteboardSecondaryMenu from '@@/vuecomponents/WhiteboardSecondaryMenu.vue'
import WhiteboardToolbar from '@@/vuecomponents/WhiteboardToolbar.vue'
import WhiteboardUploadStatusPill from '@@/vuecomponents/WhiteboardUploadStatusPill.vue'
import { shiftUpWhenOverflow } from '@@/vuecomposables/floating'
import { useTldrawCanvas } from '@@/vuecomposables/tldraw_canvas'
import { useWhiteboardPlayModePrefetchImages } from '@@/vuecomposables/use_whiteboard_play_mode_prefetch_images'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { useTldrawError } from '@@/vuecomposables/useTldrawError'
import {
  CANVAS_TEMPLATE_PREVIEW_PERSPECTIVE,
  CANVAS_TEMPLATE_PREVIEW_ROTATE_Y_DEGREE,
  useWhiteboardCanvasSize,
} from '@@/vuecomposables/useWhiteboardCanvasSize'
import { useWhiteboardDragAndDrop } from '@@/vuecomposables/useWhiteboardDragAndDrop'
import { useVirtualKeyboardHeight } from '@@/vuecomposables/virtual_keyboard_height'
import { useWhiteboardPageHandlers } from '@@/vuecomposables/whiteboard_page_handlers'
import { useWhiteboardShapeHandlers } from '@@/vuecomposables/whiteboard_shape_handlers'
import vEmitLongPress from '@@/vuedirectives/emit_long_press'
import type { ComputePositionConfig } from '@floating-ui/vue'
import { flip, offset, shift } from '@floating-ui/vue'
import { Portal } from '@linusborg/vue-simple-portal'
import { useElementBounding } from '@vueuse/core'
import { cloneDeep } from 'es-toolkit'
import { isEmpty, uniqueId } from 'es-toolkit/compat'
import { storeToRefs } from 'pinia'
import type { KeyBindingMap } from 'tinykeys'
import tinykeys from 'tinykeys'
import type { Editor, TLCameraOptions, TLPageId, TLShape, TLShapeId, VecLike } from 'tldraw'
import {
  ArrowShapeArrowheadEndStyle,
  ArrowShapeArrowheadStartStyle,
  Box,
  DefaultColorStyle,
  DefaultDashStyle,
  DefaultFillStyle,
  DefaultFontStyle,
  DefaultSizeStyle,
  GeoShapeGeoStyle,
  defaultTldrawOptions,
  react,
  setUserPreferences,
  toRichText,
} from 'tldraw'
import { computed, nextTick, onMounted, onUnmounted, provide, ref, watch } from 'vue'

const WhiteboardStartFromTemplateButton = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardStartFromTemplateButton.vue'),
)
const WhiteboardElementModifierContainer = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardElementModifierContainer.vue'),
)
const WhiteboardElementContextMenu = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardElementContextMenu.vue'),
)
const WhiteboardFrameSharePanel = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardFrameSharePanel.vue'))
const WhiteboardFrameBackgroundPanelOld = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardFrameBackgroundPanelOld.vue'),
)
const WhiteboardFrameBackgroundPanel = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardFrameBackgroundPanel.vue'),
)
const WhiteboardLinkingPanel = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardLinkingPanel.vue'))
const SurfaceWhiteboardHeader = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceWhiteboardHeader.vue'))
const WhiteboardPlayModeNavigationBar = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardPlayModeNavigationBar.vue'),
)
const WhiteboardFrameBreakoutLinksPanel = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardFrameBreakoutLinksPanel.vue'),
)
const WhiteboardFrameShareViaAnotherAppPanel = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardFrameShareViaAnotherAppPanel.vue'),
)
const WhiteboardAiChatContainer = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardAiChatContainer.vue'))

const WhiteboardCodeEditor = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardCodeEditor.vue'))
// TODO: Convert it to static import before Beta launch
const WhiteboardFrameBar = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardFrameBar.vue'))
const WhiteboardFrameBreakoutNavigationBar = defineAsyncComponent(
  () => import('@@/vuecomponents/WhiteboardFrameBreakoutNavigationBar.vue'),
)

let tldrawEditor: Editor | undefined
const tldrawAppRef = ref<HTMLElement>()
provide(TldrawAppRefKey, tldrawAppRef)

const wallContainerRef = ref()

const whiteboardStore = useWhiteboardStore()
const whiteboardPanelsStore = useWhiteboardPanelsStore()
const whiteboardToolsStore = useWhiteboardToolsStore()
const whiteboardFrameBackgroundStore = useWhiteboardFrameBackgroundStore()
const { currentFrameId, isLoadingPortraitMobileLayout } = storeToRefs(whiteboardStore)
const whiteboardScriptStore = useWhiteboardScriptStore()
const surfaceStore = useSurfaceStore()
const { xHeader, isFrozen: isWhiteboardFrozen } = storeToRefs(surfaceStore)
const { selectedTool, selectedSecondaryTool, toolStates, geoColorTldrawToolId, arrowColorTldrawToolId } =
  storeToRefs(whiteboardStore)
const { isDragging } = useWhiteboardDragAndDrop(tldrawAppRef)
const whiteboardCardTemplatesStore = useWhiteboardCardTemplatesStore()

const { hasError } = useTldrawError()

const hasTldrawLoaded = ref(false)
const isTransparentBackground = ref(false)

const whiteboardElementModifierStore = useWhiteboardElementModifierStore()
const { xContextMenu, contextMenuAnchor } = storeToRefs(whiteboardElementModifierStore)

useWhiteboardPlayModePrefetchImages()

/**
 * MOBILE LANDSCAPE
 */

/** CANVAS RESIZE AND ZOOM */
function updateMobileNotchOnOrientationChangeAndResize() {
  useSurfaceWindowMobileNotchStore().updateSafeAreaInset()
  if (screen.orientation) {
    useSurfaceWindowMobileNotchStore().updateLandscapeNotch()
  }
  surfaceContainerSizeStore.resizeSurfaceContainer({
    width: window.innerWidth,
    height: window.innerHeight,
  })
}
onMounted(() => {
  useSurfaceWindowMobileNotchStore().updateSafeAreaInset()
  // Some older browsers do not support screen.orientation
  if (screen.orientation) {
    useSurfaceWindowMobileNotchStore().updateLandscapeNotch()
    screen.orientation.addEventListener('change', updateMobileNotchOnOrientationChangeAndResize)
  }

  window.addEventListener('resize', updateMobileNotchOnOrientationChangeAndResize)
})
onUnmounted(() => {
  if (screen.orientation) {
    screen.orientation.removeEventListener('change', updateMobileNotchOnOrientationChangeAndResize)
  }
  window.removeEventListener('resize', updateMobileNotchOnOrientationChangeAndResize)
})
const surfaceContainerSizeStore = useSurfaceContainerSizeStore()
const {
  surfaceContainerWidth,
  surfaceContainerHeight,
  isLandscapePhone,
  isLandscapeTablet,
  isContainerSmallerThanTabletPortrait,
} = storeToRefs(surfaceContainerSizeStore)

const { canvasWidth, shouldTransformCanvasPerspectiveAngle, isSingleColumnToolbar, isHidingHeader } =
  useWhiteboardCanvasSize()
const { isVirtualKeyboardShowing } = useVirtualKeyboardHeight()

const whiteboardAttachmentsStore = useWhiteboardAttachmentsStore()
const whiteboardModeStore = useWhiteboardModeStore()
const whiteboardZoomLevelStore = useWhiteboardZoomLevelStore()

const canvasTemplatePreviewPaddingStart = computed(() => {
  if (surfaceContainerWidth.value <= BreakPoints.TabletLandscape) {
    return 60
  }
  if (surfaceContainerWidth.value >= BreakPoints.Desktop2XL) {
    return 240
  }
  // Linear interpolation between 60 and 240 based on container width
  return (
    60 + ((surfaceContainerWidth.value - 1024) / (BreakPoints.Desktop2XL - BreakPoints.TabletLandscape)) * (240 - 60)
  )
})

const paddingInlineStart = computed<number>(() => {
  if (!whiteboardPanelsStore.xCardTemplatesPanel || isLandscapePhone.value || isLoadingPortraitMobileLayout.value)
    return 0
  return canvasTemplatePreviewPaddingStart.value
})

const paddingInlineEnd = computed<number>(() => {
  if (!whiteboardPanelsStore.xCardTemplatesPanel || isLoadingPortraitMobileLayout.value) return 0
  if (isLandscapePhone.value) {
    return surfaceContainerWidth.value - canvasWidth.value
  }
  const CANVAS_TEMPLATE_PANEL_WIDTH = 396
  return CANVAS_TEMPLATE_PANEL_WIDTH
})

const insetInlineStart = computed(() => {
  // Why start-4.5 (18px)? 100px (frame bar) - 64px (toolbar) = 32px. Divide it by 2 to get 18.
  if (isLoadingPortraitMobileLayout.value) {
    return 0
  }
  if (
    whiteboardModeStore.isPlayMode ||
    whiteboardModeStore.isScreenshotMode ||
    shouldTransformCanvasPerspectiveAngle.value
  ) {
    return 0
  }
  const FRAME_BAR_WIDTH = 100
  const TOOLBAR_WIDTH = isSingleColumnToolbar.value ? 52 : 92
  return (FRAME_BAR_WIDTH - TOOLBAR_WIDTH) / 2
})

const insetTop = computed(() => {
  if (!isLoadingPortraitMobileLayout.value) {
    return 0
  }

  if (whiteboardPanelsStore.xCardTemplatesPanel) {
    const CARD_TEMPLATES_PANEL_HEIGHT = 540
    const HEADER_MARGIN_BOTTOM = 20
    const HEADER_HEIGHT = 72
    const availableHeight =
      surfaceContainerHeight.value - HEADER_HEIGHT - HEADER_MARGIN_BOTTOM - CARD_TEMPLATES_PANEL_HEIGHT
    // Calculate offset to prevent canvas from overlapping with whiteboard header
    const offsetTop = canvasHeight.value >= availableHeight ? canvasHeight.value - availableHeight : 0
    return isContainerSmallerThanTabletPortrait.value
      ? -(CARD_TEMPLATES_PANEL_HEIGHT + HEADER_MARGIN_BOTTOM - offsetTop) / 2
      : 0
  }

  if (isAppUsing('sandboxCardNavigation')) {
    const FRAME_BAR_HEIGHT = !whiteboardStore.isFrameBreakout ? whiteboardStore.frameBarHeight : 0
    const TOOLBAR_HEIGHT = 52
    const SPACE_FROM_TOOLBAR_TO_FRAME_BAR = 8
    const ELEMENT_MODIFIER_HEIGHT = isAppUsing('sandboxTextFormatting') && isVirtualKeyboardShowing.value ? 92 : 0
    return -(FRAME_BAR_HEIGHT + ELEMENT_MODIFIER_HEIGHT + TOOLBAR_HEIGHT + SPACE_FROM_TOOLBAR_TO_FRAME_BAR) / 2
  }

  const FRAME_BAR_HEIGHT = 100
  const TOOLBAR_HEIGHT = 58

  return (FRAME_BAR_HEIGHT - TOOLBAR_HEIGHT) / 2
})
const canvasHeight = computed(() => {
  const W_H_RATIO = 16 / 9

  return canvasWidth.value / W_H_RATIO
})

const isAllowingZooming = computed(() => {
  if ((isLandscapePhone || isLandscapeTablet) && isTouchScreen()) {
    return true
  }
  return !surfaceContainerSizeStore.isContainerBiggerThanTabletLandscape
})

const tldrawCameraOptions = computed<Partial<TLCameraOptions>>(() => ({
  isLocked: !isAllowingZooming.value, // lock the camera if we're not allowing zooming
  wheelBehavior: !isAllowingZooming.value ? 'none' : 'pan',
  zoomSteps: !isAllowingZooming.value ? [1] : [1, 2, 4, 8],
  zoomSpeed: !isAllowingZooming.value ? 0 : 1,
  constraints: {
    initialZoom: 'fit-y',
    baseZoom: 'fit-y',
    bounds: { x: 0, y: 0, w: CANVAS_WIDTH, h: CANVAS_HEIGHT },
    padding: { x: 0, y: 0 },
    origin: { x: 0, y: 0 },
    behavior: 'contain',
  },
}))

const centerZoomToBounds = () => {
  nextTick(() => {
    // Needs to be unlocked to use zoomToBounds
    tldrawEditor?.setCameraOptions({
      isLocked: false,
    })
    tldrawEditor?.zoomToBounds(new Box(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT), { inset: 0 })
    tldrawEditor?.setCameraOptions({
      ...tldrawCameraOptions.value,
    })
  })
}

// Problem: The tldraw editor sometimes breaks the viewport bounds when the tab loses focus.
// Example: User switches to a different tab then comes back to whiteboard.
// When user tries to draw at point (0, 0), it will draw at point (20, 20) instead (arbitrary values to illustrate the offset).
// Solution: Whenever the window is focused, call `updateViewportScreenBounds` to reset the viewport.
// Reference: https://discord.com/channels/859816885297741824/1237588159806836786
const forceUpdateViewportScreenBounds = () => {
  nextTick(() => {
    const rect = document.getElementById('tldraw-app')?.getBoundingClientRect()
    if (rect) {
      centerZoomToBounds()
      const next = new Box(rect.left || rect.x, rect.top || rect.y, Math.max(rect.width, 1), Math.max(rect.height, 1))
      tldrawEditor?.updateViewportScreenBounds(next)
    }
  })
}

function unselectAllShapesAndSwitchToDefaultTool(): void {
  const tldrawEditor = getEditorObject()
  const selectedShapes = tldrawEditor?.getSelectedShapes()
  if (selectedShapes && selectedShapes.length > 0) {
    tldrawEditor?.selectNone()
    whiteboardStore.selectDefaultTool()
  }
}

function handleClickingOutsideOfCanvas(): void {
  whiteboardCardTemplatesStore.dismissCardTemplatesSuggestionUI()
  unselectAllShapesAndSwitchToDefaultTool()
}

// #region TOOL SHORTCUTS REGISTERING & DEREGISTERING

/**
 * We don't want to trigger tool shortcuts while the user is typing
 */
const isActiveElementAnInputField = (): boolean => {
  const activeElement = document?.activeElement
  return isHtmlElementAnInputField(activeElement as HTMLElement) && activeElement?.getAttribute('readonly') == null
}
const isInputFocused = (): boolean => isActiveElementAnInputField()

const surfacePanelsStore = useSurfacePanelsStore()
const surfaceSettingsStore = useSurfaceSettingsStore()
// Has to be a function to be able to check if the input is focused since it's not reactive
const checkIfShortcutsDisallowed = () =>
  isInputFocused() ||
  xScriptEditor.value ||
  xContextMenu.value ||
  surfaceSettingsStore.activePanel !== null ||
  surfacePanelsStore.xSurfaceSharePanel

const whiteboardPlayModeNavigationBarRef = ref<typeof WhiteboardPlayModeNavigationBar | null>(null)
function addShortcutsListener(): () => void {
  /**
   * FROZEN
   */
  if (surfaceStore.isFrozen && !whiteboardModeStore.isPlayMode) {
    return () => {}
  }

  /**
   * CODE MODE
   */
  if (whiteboardModeStore.isCodeMode) {
    return () => {}
  }

  /**
   * PLAY MODE
   */
  if (whiteboardModeStore.isPlayMode) {
    if (whiteboardStore.isFrameBreakout) {
      // We should not let users navigate to other frames in breakout mode
      return () => {}
    }
    return tinykeys(window, {
      ArrowLeft: () => {
        trackEvent('Whiteboard', 'Pressed ArrowLeft Shortcut', null, null, { context: 'Play mode' })
        handleAction(ActionType.GoToPreviousPage, () => true)
      },
      ArrowRight: () => {
        trackEvent('Whiteboard', 'Pressed ArrowRight Shortcut', null, null, { context: 'Play mode' })
        handleAction(ActionType.GoToNextPage, () => true)
      },
      ArrowUp: () => {
        trackEvent('Whiteboard', 'Pressed ArrowUp Shortcut', null, null, { context: 'Play mode' })
        handleAction(ActionType.GoToPreviousPage, () => true)
      },
      ArrowDown: () => {
        trackEvent('Whiteboard', 'Pressed ArrowDown Shortcut', null, null, { context: 'Play mode' })
        handleAction(ActionType.GoToNextPage, () => true)
      },
      PageUp: () => {
        trackEvent('Whiteboard', 'Pressed PageUp Shortcut', null, null, { context: 'Play mode' })
        handleAction(ActionType.GoToPreviousPage, () => true)
      },
      PageDown: () => {
        trackEvent('Whiteboard', 'Pressed PageDown Shortcut', null, null, { context: 'Play mode' })
        handleAction(ActionType.GoToNextPage, () => true)
      },
      Space: () => {
        // If user is on any UI on navigation bar and they hit the space key,
        // we only want the click event to happen and not the forward shortcut key event
        if (whiteboardPlayModeNavigationBarRef.value?.$el?.contains(document.activeElement)) {
          return
        }
        trackEvent('Whiteboard', 'Pressed Space Shortcut', null, null, { context: 'Play mode' })
        handleAction(ActionType.GoToNextPage, () => true)
      },
    })
  }

  /**
   * DRAW MODE
   */

  const keybindingMap: KeyBindingMap = {}
  const primaryShortcuts = whiteboardToolbarStore.flattenedDisplayedTools
    .filter((tool) => {
      if (tool === undefined) {
        captureMessage('tool is undefined', {
          context: {
            flattenedDisplayedTools: whiteboardToolbarStore.flattenedDisplayedTools,
            allEnabledToolIds: whiteboardToolsStore.allEnabledToolIds,
          },
        })
        return false
      }
      return tool.shortcut
    })
    .map((tool) => tool.shortcut)
  const secondaryShortcuts = whiteboardToolbarStore.flattenedDisplayedTools
    .map((tool) => tool.secondaryMenu?.items ?? [])
    .flat()
    .filter((tool) => tool.shortcut)
    .map((tool) => tool.shortcut)

  keybindingMap.Escape = () => {
    // Do not conditionally disable this shortcut otherwise there's no way to dismiss the menu with keys
    if (xContextMenu.value) {
      whiteboardElementModifierStore.hideContextMenu()
    } else if (whiteboardStore.selectedToolId !== 'select') {
      // Pressing Esc switches the current tool to Select
      whiteboardStore.selectTool('select')
    }

    if (
      whiteboardCardTemplatesStore.xSuggestedCardTemplatesRow ||
      whiteboardCardTemplatesStore.xStartFromATemplateButton
    ) {
      whiteboardCardTemplatesStore.dismissCardTemplatesSuggestionUI()
    }
  }

  actionShortcuts.forEach((shortcut) => {
    keybindingMap[shortcut.kbd] = (event) => {
      if (!checkIfShortcutsDisallowed()) {
        const editor = getEditorObject()
        const selectedShapes = editor?.getSelectedShapes() ?? []

        // Disable grouping if any shape is pending approval
        if (
          (shortcut.actionType === ActionType.Group || shortcut.actionType === ActionType.Ungroup) &&
          !shouldEnableGroupingForShapes(selectedShapes)
        ) {
          return
        }

        const areSelectedShapesTypesAllowedToBeCreated =
          whiteboardPermissionsStore.canIAdminister ||
          selectedShapes.every((shape) => whiteboardToolsStore.wasShapeCreatedFromAllowedTool(shape))

        // If user is pressing a keyboard shortcut on selected shapes that aren't allowed to be created,
        // don't perform the action.
        if (
          selectedShapes.length > 0 &&
          !areSelectedShapesTypesAllowedToBeCreated &&
          [ActionType.Cut, ActionType.Copy, ActionType.Duplicate].includes(shortcut.actionType)
        ) {
          return
        }
        trackEvent('Whiteboard', `Pressed ${shortcut.actionType} Shortcut`, null, null, { context: 'Draw mode' })
        event.preventDefault()
        handleBoardAction(shortcut.actionType)
      }
    }
  })

  primaryShortcuts.forEach((shortcut) => {
    if (!shortcut) {
      return
    }
    keybindingMap[shortcut] ??= () => {
      if (checkIfShortcutsDisallowed()) {
        return
      }
      const tool = whiteboardToolbarStore.flattenedDisplayedTools.find((tool) => tool.shortcut === shortcut)
      if (tool && whiteboardStore.selectedToolId !== tool.id) {
        trackEvent('Whiteboard', `Pressed ${shortcut} Shortcut`, null, null, { context: 'Draw mode' })
        whiteboardStore.selectTool(tool.id)
        // Clear the selected toolbar button since we don't have access to it from a shortcut
        whiteboardStore.setSelectedToolbarButtonEl(null)
      }
    }
  })

  secondaryShortcuts.forEach((shortcut) => {
    if (!shortcut) {
      return
    }
    keybindingMap[shortcut] ??= () => {
      if (checkIfShortcutsDisallowed()) {
        return
      }
      const tool = whiteboardToolbarStore.flattenedDisplayedTools.find((tool) =>
        tool.secondaryMenu?.items.find((item) => item?.shortcut === shortcut),
      )
      if (tool) {
        const secondaryTool = tool.secondaryMenu?.items.find((item) => item?.shortcut === shortcut)
        if (secondaryTool) {
          trackEvent('Whiteboard', `Pressed ${shortcut} Shortcut`, null, null, { context: 'Draw mode' })
          whiteboardStore.selectTool(tool.id)
          // Clear the selected toolbar button since we don't have access to it from a shortcut
          whiteboardStore.setSelectedToolbarButtonEl(null)
          whiteboardStore.selectSecondaryTool(secondaryTool.id)
        }
      }
    }
  })

  keybindingMap.c = () => {
    if (checkIfShortcutsDisallowed()) {
      return
    }
    if (whiteboardStore.currentFrameId == null) {
      return
    }

    trackEvent('Whiteboard', 'Pressed C Shortcut', null, null, { context: 'Draw mode' })
    whiteboardStore.addFrameBelow(whiteboardStore.currentFrameId)
  }

  return tinykeys(window, keybindingMap)
}

let removeShortcutsListener = () => {}

onMounted(async () => {
  window.addEventListener('focus', forceUpdateViewportScreenBounds)
  await nextTick()
  removeShortcutsListener = addShortcutsListener()
})

watch(
  [() => surfaceStore.isFrozen, () => whiteboardModeStore.isPlayMode, () => whiteboardModeStore.isCodeMode],
  async () => {
    removeShortcutsListener()
    // await nextTick()
    // TODO: Somehow the nextTick causes the listeners to be registered twice.
    removeShortcutsListener = addShortcutsListener()
  },
)

// #endregion

// Setup native bridge listeners
onMounted(() => {
  if (device.app) {
    import('@@/vuecomposables/native_bridge_listeners').then(({ useNativeBridgeListeners }) => {
      useNativeBridgeListeners({ for: 'whiteboard' })
    })
  }
})

onUnmounted(() => {
  window.removeEventListener('focus', forceUpdateViewportScreenBounds)
  removeShortcutsListener()
})

let checkCanEdit: (shape: TLShape) => boolean = () => false

function onTldrawCanvasMounted(editor: Editor) {
  const top = tldrawAppRef.value?.getBoundingClientRect().top ?? 0
  canvasTop.value = top

  /**
   * WATCHERS
   *
   * Call all watchers here to avoid the case where tldraw is not initialized yet but we already ran
   * watcher logic that depends on tldraw being initialized.
   */

  watch(geoColorTldrawToolId, (tldrawColor) => {
    if (tldrawColor == null) {
      return
    }
    editor.setStyleForNextShapes(DefaultColorStyle, tldrawColor)
  })

  watch(arrowColorTldrawToolId, (tldrawColor) => {
    if (tldrawColor == null) {
      return
    }
    editor.setStyleForNextShapes(DefaultColorStyle, tldrawColor)
  })

  watch(
    [selectedTool, selectedSecondaryTool, toolStates],
    () => {
      // Ref: https://linear.app/padlet/issue/PRO-15919/long-term-fix-cannot-change-atoms-during-reaction-cycle
      editor.run(() => {
        const newTool = whiteboardStore.selectedTool
        const newSecondaryTool = whiteboardStore.selectedSecondaryTool
        const newToolStates = whiteboardStore.toolStates

        if (whiteboardStore.ignoreTldrawTrigger) {
          whiteboardStore.ignoreTldrawTrigger = false
          return
        }
        if (newSecondaryTool?.tldrawToolId === 'padlet-speech-bubble') {
          // Emulate the logic for the geo tool
          editor.setStyleForNextShapes(DefaultSizeStyle, isAppUsing('sandboxTextSizeModifier') ? 's1' : 'm')
          editor.setCurrentTool('padlet-speech-bubble')
          editor.setStyleForNextShapes(DefaultFillStyle, newToolStates.geo.fillStyle ?? 'semi')
          if (geoColorTldrawToolId.value) {
            editor.setStyleForNextShapes(DefaultColorStyle, geoColorTldrawToolId.value)
          }
          if (isAppUsing('sandboxTextFonts')) {
            editor.setStyleForNextShapes(DefaultFontStyle, whiteboardStore.defaultTldrawFontId)
          }
          return
        }

        const tldrawToolId = newTool?.tldrawToolId ?? 'draw'
        // Geo tool
        // Switch to the geo tool in Tldraw depends on the selected secondary,
        // and set the color based on geo color menu

        if (tldrawToolId === 'geo') {
          const tldrawShapeId = whiteboardStore.selectedSecondaryTool?.tldrawToolId
          if (tldrawShapeId == null) {
            return
          }
          editor.updateInstanceState(
            {
              stylesForNextShape: {
                ...editor.getInstanceState().stylesForNextShape,
                [GeoShapeGeoStyle.id]: tldrawShapeId,
              },
            },
            { history: 'ignore' },
          )
          editor.setStyleForNextShapes(DefaultSizeStyle, isAppUsing('sandboxTextSizeModifier') ? 's1' : 'm')

          editor.setCurrentTool('geo')
          editor.setStyleForNextShapes(DefaultFillStyle, newToolStates.geo.fillStyle ?? 'semi')
          if (geoColorTldrawToolId.value) {
            editor.setStyleForNextShapes(DefaultColorStyle, geoColorTldrawToolId.value)
          }
          if (isAppUsing('sandboxTextFonts')) {
            editor.setStyleForNextShapes(DefaultFontStyle, whiteboardStore.defaultTldrawFontId)
          }
          return
        }

        if (tldrawToolId === 'arrow') {
          const tldrawShapeId = whiteboardStore.selectedSecondaryTool?.tldrawToolId
          if (tldrawShapeId == null) {
            return
          }
          // For arrows, the secondary tool id is a combination of arrowStart, dash and arrowEnd in kebab case
          const [arrowStart, dash, arrowEnd] = tldrawShapeId.split('-')
          editor.updateInstanceState(
            {
              stylesForNextShape: {
                ...editor.getInstanceState().stylesForNextShape,
                [ArrowShapeArrowheadStartStyle.id]: arrowStart,
                [DefaultDashStyle.id]: dash,
                [ArrowShapeArrowheadEndStyle.id]: arrowEnd,
              },
            },
            { history: 'ignore' },
          )

          editor.setCurrentTool('arrow')
          if (arrowColorTldrawToolId.value) {
            editor.setStyleForNextShapes(DefaultColorStyle, arrowColorTldrawToolId.value)
          }
          if (isAppUsing('sandboxTextFonts')) {
            editor.setStyleForNextShapes(DefaultFontStyle, whiteboardStore.defaultTldrawFontId)
          }
          return
        }

        // We also override the default text size in the tldraw patch to handle double clicks on canvas
        if (tldrawToolId === 'text') {
          editor.setStyleForNextShapes(DefaultSizeStyle, isAppUsing('sandboxTextSizeModifier') ? 'm1' : 'l')
        } else {
          editor.setStyleForNextShapes(DefaultSizeStyle, isAppUsing('sandboxTextSizeModifier') ? 's1' : 'm')
        }

        if (isAppUsing('sandboxTextFonts')) {
          editor.setStyleForNextShapes(DefaultFontStyle, whiteboardStore.defaultTldrawFontId)
        }

        // Other tools
        // Switch to the tool in Tldraw, and set the color
        const color = newSecondaryTool?.tldrawToolId as TldrawDefaultColorStyleColor | undefined

        if (tldrawToolId === 'draw-nested') {
          if (newTool == null || newSecondaryTool == null) {
            return
          }
          editor.setCurrentTool(newSecondaryTool.tldrawToolId ?? 'draw')
          const actualSecondaryToolColorId = whiteboardStore.toolCurrentColors[newTool.id]
          const tldrawToolId = getTldrawToolId(actualSecondaryToolColorId as string)
          editor.setStyleForNextShapes(DefaultColorStyle, tldrawToolId as TldrawDefaultColorStyleColor)
          editor.setStyleForNextShapes(DefaultDashStyle, 'draw')
          return
        }
        editor.setCurrentTool(tldrawToolId)
        // Otherwise drawing a shape will fill after using geo
        editor.setStyleForNextShapes(DefaultFillStyle, 'none')
        if (color) {
          editor.setStyleForNextShapes(DefaultColorStyle, color)
        }
      })
    },
    { immediate: true },
  )

  watch(
    [isReadOnlyMode, () => whiteboardModeStore.isCodeMode],
    ([isReadOnlyMode, isCodeMode]) => {
      if (isCodeMode) {
        editor.updateInstanceState({ isReadonly: true })
        whiteboardStore.selectTool('select')
      } else if (isReadOnlyMode) {
        editor.updateInstanceState({ isReadonly: true })
        whiteboardStore.selectTool('interact')
      } else {
        editor.updateInstanceState({ isReadonly: false })
        whiteboardStore.selectDefaultTool()
      }
      nextTick(() => {
        removeShortcutsListener()
        removeShortcutsListener = addShortcutsListener()
      })
    },
    { immediate: true },
  )

  watch(selectedTool, (newTool) => {
    const tldrawToolId = newTool?.tldrawToolId

    if (tldrawToolId === 'interact') {
      hideAllUnapprovedShapesTemporarily()
      hideAllUnapprovedIndicators()
    } else {
      unhideAllUnapprovedShapes()
      unhideAllUnapprovedIndicators()
    }
  })

  watch(isAllowingZooming, (isAllowing) => {
    if (isAllowing) {
      nextTick(() => {
        forceUpdateViewportScreenBounds()
      })
    }
  })

  // When page changes, the newly opened page won't have the correct zoom level.
  // So we need to re-zoom to the correct level.
  watch([currentFrameId, canvasWidth, canvasHeight], () => {
    centerZoomToBounds()
  })

  watch(
    () => whiteboardToolbarStore.flattenedDisplayedTools,
    () => {
      removeShortcutsListener()
      removeShortcutsListener = addShortcutsListener()
    },
  )
  // When code mode is on, we need to re-zoom to the correct level after resizing the canvas.
  watch(
    () => whiteboardModeStore.isCodeMode,
    (isCodeMode) => {
      if (!isCodeMode) {
        return
      }
      forceUpdateViewportScreenBounds()
    },
  )

  /**
   * SYNC TLDRAW STATE TO VUE REACTIVE STATE
   *
   * All syncing for **global** state should be done here to ensure that
   * we only start rendering our UI after we have started listening
   * to all relevant global state to prevent race conditions.
   *
   * We do the syncing before anything else to ensure that there aren't any updates
   * to the editor state before we start listening.
   */

  // NOTE: This composable allows us to register reactive effects that depend on the Tldraw state
  // anywhere in the Vue app. From now on, you don't need to put `react` calls here anymore.
  // Calling the `set` function to signify that Tldraw has mounted and the editor instance is ready.
  useTldrawEditor().set(editor)

  // TODO: Move all these reactive effects to where they make sense e.g. in Pinia stores, relevant components, etc.

  /**
   * After using certain tools, e.g. geometry or shape, the editor will change the current tool back to Select automatically.
   * We track the change here and update our store value to match the editor value.
   *
   * Why not just check if the path starts with 'select'?
   * There are intermediate states (e.g. 'select.resizing') that the user could cancel from (e.g. Esc).
   * In those cases, we don't want to switch to Select yet to preserve the original behavior.
   * Also, we should not update the toolbar selected tool until the pointer is up.
   */
  if (!isAppUsing('sandboxRetainActiveTool')) {
    react('set store tool to Select, if needed', () => {
      if (editor.isInAny('select') && whiteboardStore.selectedToolId !== 'select') {
        whiteboardStore.selectToolWithoutTriggeringTldraw('select')
      }
    })
  } else {
    react('retain active tool after adding shape', () => {
      if (!editor.isInAny('select.pointing_canvas')) return
      const toolMap = {
        text: 'text',
        note: 'note',
        geo: 'geo',
        arrow: 'arrow',
      }
      const selectedTool = whiteboardStore.selectedToolId
      if (selectedTool in toolMap) {
        editor.setCurrentTool(toolMap[selectedTool])
      }
      if (whiteboardStore.selectedSecondaryTool?.tldrawToolId === 'padlet-speech-bubble') {
        editor.setCurrentTool('padlet-speech-bubble')
      }
    })
  }

  react('get selected object and check for script', () => {
    if (editor.isInAny('select')) {
      const selectedShapes = editor.getSelectedShapes()
      const selectedShape = selectedShapes.length === 1 ? selectedShapes[0] : null // If multiple shapes are selected, we don't show the script editor
      selectedShapeIdForScript.value = selectedShape?.id
      xOpenScriptEditorButton.value = selectedShape?.id != null
    } else {
      xOpenScriptEditorButton.value = false
    }
  })

  // @ts-ignore
  editor.on('interact:shape', (event: { shapeId; eventName }) => {
    if (event?.shapeId == null) {
      return
    }
    if (isAppUsing('sandboxCode')) {
      import('@@/bits/whiteboard_blockly_script_executor').then(({ runScript }) => {
        runScript(whiteboardScriptStore.scripts[event.shapeId], event.eventName)
      })
    }
    triggerLinking(editor, whiteboardLinkingStore.linkings[event.shapeId])
    if (event.eventName === 'click') {
      useWhiteboardSpecialEffectsStore().triggerSpecialEffectForShape(event.shapeId)
    }
  })

  react('update frame state with tldraw pages data', () => {
    const pages = editor.getPages()

    const newFrames = pages
      .map((page) => {
        return {
          id: page.id,
          name: page.name,
        }
      })
      .filter((page) => !!page?.id) // to filter out the observer object before assigning to state

    // Update frames and merge with existing frames img
    whiteboardStore.frames = newFrames
  })

  react('set undo and redo capability', () => {
    const canUndo = editor.getCanUndo()
    const canRedo = editor.getCanRedo()
    whiteboardStore.canIUndo = canUndo
    whiteboardStore.canIRedo = canRedo
  })

  react('set transparent background based on current page meta', () => {
    const currentFrame = editor.getCurrentPage()
    const currentFrameMeta = currentFrame?.meta as PageMeta

    const currentBackgroundType = currentFrameMeta.backgroundType ?? BackgroundType.Plain
    isTransparentBackground.value = currentBackgroundType === BackgroundType.Frosted
  })

  react('allow drawing with finger after using stylus', () => {
    if (editor.isInAny('select.idle') && editor.getInstanceState().isPenMode) {
      editor.updateInstanceState({ isPenMode: false })
    }
  })

  react('listen to zoom level changes', () => {
    whiteboardZoomLevelStore.zoomLevel = editor.getZoomLevel()
  })

  react('listen to background changes', () => {
    // The displayedFrameBackground is outdated when the background is set from other sessions
    // and the frame background is not yet synced with the frame meta.
    // In this case, we need to sync displayedFrameBackgrounds with frame meta
    const pages = editor.getPages()
    const hasFrameBackgroundChanged = pages.some((page) => {
      const backgroundType = page.meta.backgroundType as string
      const displayedBackground = whiteboardFrameBackgroundStore.displayedFrameBackgrounds[page.id]
      return (
        displayedBackground?.id !== backgroundType ||
        displayedBackground?.url !== whiteboardFrameBackgroundStore.getPageBackgroundUrl(page)
      )
    })

    if (hasFrameBackgroundChanged) {
      whiteboardFrameBackgroundStore.syncOriginalFrameBackgroundsWithFrameMeta()
    }
  })

  whiteboardStore.selectDefaultTool()
  initializeLinking(editor)
  listenToLinkingChanges(editor)

  whiteboardFrameBackgroundStore.initializeFrameBackground(editor)

  if (isAppUsing('sandboxTextFonts')) {
    migrateExistingTextShapesToUseThemeFont()
  }

  /**
   * SHAPE APPROVAL
   */
  react('update unapproved shapes', () => {
    const editor = getEditorObject()
    if (editor == null) {
      whiteboardStore.unapprovedShapes = []
      return
    }

    const unapprovedShapes = editor
      .getPages()
      .flatMap((page) => Array.from(editor.getPageShapeIds(page.id)))
      .map((shapeId) => editor.getShape(shapeId))
      .filter((shape) => !isShapeApproved(shape)) as TLShape[]
    whiteboardStore.unapprovedShapes = cloneDeep(unapprovedShapes)
    whiteboardStore.timesUnapprovedShapesUpdated = whiteboardStore.timesUnapprovedShapesUpdated + 1
  })

  /**
   * BREAKOUT LINK / FRAME LINK
   * Do it before we update the viewport screen bounds, so the camera is correctly bounded
   */
  let frameId
  if (whiteboardStore.isFrameBreakout) {
    const urlObj = new URL(window.location.href)
    const pathSegments = urlObj.pathname.split('/')
    // frameBreakoutIdentifier format: {wallHashidWithoutPrefix}-{frameId}
    // Note: The frameId itself may contain -
    const frameIdFromPath = pathSegments[pathSegments.length - 1].replace(
      `${surfaceStore.wallHashidWithoutPrefix}-`,
      '',
    )
    // Value is not automatically decoded unlike URLSearchParams
    frameId = decodeURIComponent(frameIdFromPath)
  } else {
    // Handle link to specific frame (not breakout), frameId is a query param instead
    const searchParams = new URLSearchParams(window.location.search)
    frameId = searchParams.get(FRAME_ID_QUERY_PARAM) || ''
  }

  // Update current frame if exists
  const matchingFrame = editor.getPage(frameId as TLPageId)
  if (matchingFrame) {
    // We don't want users to be able to undo to reach the initial frame
    editor.run(
      () => {
        editor.setCurrentPage(matchingFrame)
      },
      { history: 'ignore' },
    )
  }

  /**
   * SETUP FINITE CANVAS
   */
  editor.setCameraOptions({
    ...tldrawCameraOptions.value,
  })
  centerZoomToBounds()

  /**
   * ADD NAMES TO REALTIME CURSOR
   */
  const name =
    surfaceStore.user.display_name ??
    surfaceStore.user.short_name ??
    surfaceStore.user.name ??
    surfaceStore.user.username
  setUserPreferences({
    id: String(surfaceStore.user.id) ?? uniqueId('anon-'),
    name,
  })

  useWhiteboardPageHandlers(editor).registerSideEffects()

  /**
   * ADD NAMES TO REALTIME CURSOR
   */
  const whiteboardShapeHandlers = useWhiteboardShapeHandlers(editor)
  checkCanEdit = whiteboardShapeHandlers.checkCanEdit

  // Upload with pasting and dropping

  editor.registerExternalContentHandler('files', async (content) => {
    if (shouldEnableWhiteboardInPlaceUploadingAnimation()) {
      if (!whiteboardPermissionsStore.canIWriteFrame(whiteboardStore.currentFrameId as TLPageId)) return
      const fileInfos = await calculateUploadedFilePositionsAndDimensions({
        files:
          content.files.length > MAXIMUM_NUMBER_OF_FILES
            ? content.files.slice(0, MAXIMUM_NUMBER_OF_FILES)
            : content.files,
        dropPosition: { x: content.point?.x ?? 0, y: content.point?.y ?? 0 },
        canvasWidth: canvasWidth.value,
        canvasHeight: canvasHeight.value,
      })
      fileInfos.forEach((info) => {
        const { file, width, height, aspectRatio, x, y } = info
        whiteboardUploadStore.startNewFileUpload(file, {
          contentSource: ContentPickerSource.FileBrowser,
          x,
          y,
          width,
          height,
          aspectRatio,
        })
      })
    } else {
      content.files.forEach((file) => {
        whiteboardUploadStore.startNewFileUploadOld(file, { contentSource: ContentPickerSource.FileBrowser })
      })
    }
  })

  function createShapeForTextOrUrl(text: string, point?: VecLike): void {
    try {
      const url = new URL(text)
      if (shouldEnableWhiteboardInPlaceUploadingAnimation()) {
        whiteboardUploadStore.startNewUrlUpload(url.toString(), { contentSource: ContentPickerSource.LinkInput })
      } else {
        whiteboardUploadStore.startNewUrlUploadOld(url.toString(), { contentSource: ContentPickerSource.LinkInput })
      }
    } catch (error) {
      // Not a URL
      editor.createShape({
        type: 'text',
        props: {
          richText: toRichText(text),
        },
        x: point?.x,
        y: point?.y,
      })
    }
  }

  // Script editor/Code mode

  editor.registerExternalContentHandler('embed', async (content) => {
    createShapeForTextOrUrl(content.url, content.point)
  })

  editor.registerExternalContentHandler('text', async (content) => {
    createShapeForTextOrUrl(content.text, content.point)
  })

  editor.registerExternalContentHandler('url', async (content) => {
    createShapeForTextOrUrl(content.url, content.point)
  })

  editor.registerExternalContentHandler('svg-text', async (content) => {
    const svgFile = new File([content.text], 'svg-text.svg', { type: 'image/svg+xml' })
    whiteboardUploadStore.startNewFileUploadOld(svgFile, { contentSource: ContentPickerSource.FileBrowser })
  })

  tldrawEditor = window.$tldrawEditor!

  useWhiteboardSpecialEffectsStore().preloadSpecialEffectsWhenEnterPlayMode()

  // Clean up failed uploads
  deleteFailedUploads()
  hasTldrawLoaded.value = true
}

useTldrawCanvas({
  elementId: 'tldraw-app',
  onMount: onTldrawCanvasMounted,
})

// Secondary Menu

const toolbar = ref<typeof WhiteboardToolbar | null>(null)
const toolbarEl = computed<HTMLElement | undefined>(() => toolbar.value?.$el)

const whiteboardToolbarStore = useWhiteboardToolbarStore()
const isRtl = dir() === 'rtl'
const secondaryMenuPopoverConfig = computed<Partial<ComputePositionConfig>>(() => {
  return !isLoadingPortraitMobileLayout.value
    ? {
        placement: isRtl ? 'right' : 'left',
        middleware: [
          offset({
            mainAxis: whiteboardToolbarStore.secondaryMenuMainAxisOffset,
            crossAxis: whiteboardToolbarStore.secondaryMenuCrossAxisOffset,
          }),
          shiftUpWhenOverflow({ distanceFromViewportEdges: 4 }),
        ],
      }
    : {
        placement: 'top',
        middleware: [
          offset({
            mainAxis: 8,
            crossAxis: whiteboardToolbarStore.secondaryMenuCrossAxisOffset,
          }),
          shift(),
        ],
      }
})

// Script Editor
// Code mode
const xScriptEditor = ref(false)
const selectedShapeIdForScript = ref<string | undefined>(undefined)
const xOpenScriptEditorButton = ref(false)
const isScriptEditorEnabled = computed(() => whiteboardModeStore.isCodeMode)
const whiteboardObjectPreviewStore = useWhiteboardObjectPreviewStore()
onMounted(() => {
  // Set isScriptEditorEnabled to true if the url params include script_editor=true
  // Color should be changed before tldraw is initialized
  changeAllTldrawDefaultColorStyleColor()
  // Initialize scripts, update initial previews and initialize blockly
  whiteboardScriptStore.initializeWhiteboardScripts().then(() => {
    whiteboardObjectPreviewStore.updateObjectPreviews(Object.keys(whiteboardScriptStore.scripts) as TLShapeId[])
    if (isAppUsing('sandboxCode')) {
      import('@@/bits/blockly').then(({ initialize }) =>
        initialize({
          getCardList: () => whiteboardStore.frames.map((frame, index) => [String(index + 1), frame.id]),
        }),
      )
    }
  })
})

function saveScript(script: string) {
  if (!selectedShapeIdForScript.value) {
    return
  }
  whiteboardScriptStore.upsertScript(selectedShapeIdForScript.value, script)
  if (!whiteboardObjectPreviewStore.objectIdsWithPreviews.includes(selectedShapeIdForScript.value as TLShapeId)) {
    whiteboardObjectPreviewStore.updateObjectPreviews([selectedShapeIdForScript.value as TLShapeId])
  }
  xScriptEditor.value = false
}
const initialScript = computed(() => {
  if (!selectedShapeIdForScript.value) {
    return ''
  }
  return whiteboardScriptStore.scripts[selectedShapeIdForScript.value]
})

/**
 * CONTEXT MENU
 */

const contextMenuRef = ref<typeof OzBasePopover | null>(null)
const contextMenuEl = computed(() => contextMenuRef.value?.$el)

const checkAndDismissContextMenu = (e: PointerEvent): void => {
  if (contextMenuEl.value?.contains(e.target)) {
    // Let the context menu click handlers dismiss the menu instead
    return
  }
  whiteboardElementModifierStore.hideContextMenu()
}

const handleTldrawAppPointerDown = (e: PointerEvent): void => {
  checkAndDismissContextMenu(e)
  whiteboardCardTemplatesStore.dismissCardTemplatesSuggestionUI()
}

function handleBoardAction(actionType: ActionType, selectedShapes?: TLShape[]) {
  if (actionType === ActionType.Link) {
    openLinkingPanel()
    return
  }
  if (actionType === ActionType.SpecialEffect) {
    useWhiteboardSpecialEffectsStore().openSpecialEffectsModal()
    return
  }
  handleAction(actionType, checkCanEdit, selectedShapes)
}

/*
 * FRAME BAR
 */

const whiteboardContainerRef = ref<HTMLElement | null>(null)
const { top: containerTop, bottom: containerBottom } = useElementBounding(whiteboardContainerRef)
const { height: toolbarHeight, top: toolbarTop } = useElementBounding(toolbarEl)
const { top: canvasTop } = useElementBounding(tldrawAppRef)

const frameBarMaxHeight = computed(() => {
  // Height of canvas
  return Math.max(canvasHeight.value, toolbarEl.value?.getBoundingClientRect().height ?? 0)
})
const xFrameBar = computed(() => {
  return (
    isAppUsing('sandboxCardNavigation') &&
    !hasError.value &&
    hasTldrawLoaded.value &&
    !whiteboardStore.isFrameBreakout &&
    !whiteboardModeStore.isFramePreviewMode &&
    !whiteboardModeStore.isFullWidthFramePreviewMode &&
    !whiteboardModeStore.isScreenshotMode &&
    whiteboardModeStore.isDrawMode
  )
})
const xFrameBreakoutNavigationBar = computed(() => {
  return (
    isAppUsing('sandboxCardNavigation') &&
    !hasError.value &&
    hasTldrawLoaded.value &&
    whiteboardStore.isFrameBreakout &&
    !whiteboardModeStore.isFramePreviewMode &&
    !whiteboardModeStore.isFullWidthFramePreviewMode &&
    !whiteboardModeStore.isScreenshotMode &&
    surfaceContainerSizeStore.isLandscapePhone
  )
})

// Watcher to update the card template preview animation state.
watch(
  () => whiteboardPanelsStore.xCardTemplatesPanel,
  async () => {
    if (whiteboardContainerRef.value == null) return
    whiteboardCardTemplatesStore.isTemplatePreviewUIAnimating = true
    await awaitTransitionEnd(whiteboardContainerRef.value)

    // Fix an issue with `useElementBounding(tldrawAppRef)` not returning the latest top value
    // after the card templates preview animation.
    const top = tldrawAppRef.value?.getBoundingClientRect()?.top
    if (top != null) {
      canvasTop.value = top
    }

    // Wait for DOM updates to complete before marking the animation as done.
    await awaitDOMUpdates()
    whiteboardCardTemplatesStore.isTemplatePreviewUIAnimating = false
  },
)

/**
 * FRAME CONTEXT MENU
 */

enum FrameContextMenuEvent {
  CopyLinkToFrame = 'CopyLinkToFrame',
  CopyFrameBreakoutLink = 'CopyFrameBreakoutLink',
  ChangeBackground = 'ChangeBackground',
  ShareAndPermissions = 'ShareAndPermissions',
  MoveFrameUp = 'MoveFrameUp',
  MoveFrameDown = 'MoveFrameDown',
  AddFrameAbove = 'AddFrameAbove',
  AddFrameBelow = 'AddFrameBelow',
  Duplicate = 'Duplicate',
  ClearFrame = 'ClearFrame',
  DeleteFrame = 'DeleteFrame',
}

interface FrameContextMenuEventPayload {
  action: FrameContextMenuEvent
  frameId: string
}

const xFrameContextMenu = ref(false)
const frameContextMenuAnchor = ref<Anchor>()
const frameContextMenuGroups = ref<OzMenuRowsRow<FrameContextMenuEventPayload>[][]>([])

const buildFrameContextMenu = (anchor: Anchor, frameId: string) => {
  xFrameContextMenu.value = true
  frameContextMenuAnchor.value = anchor
  const canManageFrame = whiteboardPermissionsStore.canIManageFrame(frameId as TLPageId)
  const canEditFrame = whiteboardPermissionsStore.canIEditFrame(frameId as TLPageId)

  const menuGroups: OzMenuRowsRow[][] = [
    [
      {
        icon: 'share_arrow_outline',
        text: whiteboardPermissionsStore.canIAdminister ? __('Sharing & permissions') : __('Sharing'),
        eventPayload: { action: FrameContextMenuEvent.ShareAndPermissions, frameId },
      },
      {
        icon: 'picture_frame',
        text: __('Change background'),
        eventPayload: { action: FrameContextMenuEvent.ChangeBackground, frameId },
        shouldShow: canEditFrame,
        isDisabled: isWhiteboardFrozen.value,
        rowStyle: isWhiteboardFrozen.value ? OzMenuRowsRowStyle.Disabled : undefined,
      },
    ].filter((item) => item.shouldShow !== false),
    [
      {
        icon: 'move_up',
        text: __('Move before'),
        eventPayload: {
          action: FrameContextMenuEvent.MoveFrameUp,
          frameId,
        },
        shouldShow: !whiteboardStore.isFirstFrame(frameId) && canManageFrame,
        isDisabled: isWhiteboardFrozen.value,
        rowStyle: isWhiteboardFrozen.value ? OzMenuRowsRowStyle.Disabled : undefined,
      },
      {
        icon: 'move_down',
        text: __('Move after'),
        eventPayload: {
          action: FrameContextMenuEvent.MoveFrameDown,
          frameId,
        },
        shouldShow: !whiteboardStore.isLastFrame(frameId) && canManageFrame,
        isDisabled: isWhiteboardFrozen.value,
        rowStyle: isWhiteboardFrozen.value ? OzMenuRowsRowStyle.Disabled : undefined,
      },
      {
        icon: 'new_frame_above',
        text: __('Add a card above'),
        eventPayload: {
          action: FrameContextMenuEvent.AddFrameAbove,
          frameId,
        },
        shouldShow: canManageFrame,
        isDisabled: isWhiteboardFrozen.value,
        rowStyle: isWhiteboardFrozen.value ? OzMenuRowsRowStyle.Disabled : undefined,
      },
      {
        icon: 'new_frame_below',
        text: __('Add a card below'),
        eventPayload: {
          action: FrameContextMenuEvent.AddFrameBelow,
          frameId,
        },
        shouldShow: canManageFrame,
        isDisabled: isWhiteboardFrozen.value,
        rowStyle: isWhiteboardFrozen.value ? OzMenuRowsRowStyle.Disabled : undefined,
      },
    ].filter((item) => item.shouldShow !== false),
    [
      {
        icon: 'duplicate_outline',
        text: __('Duplicate'),
        eventPayload: {
          action: FrameContextMenuEvent.Duplicate,
          frameId,
        },
        shouldShow: canManageFrame,
        isDisabled: isWhiteboardFrozen.value,
        rowStyle: isWhiteboardFrozen.value ? OzMenuRowsRowStyle.Disabled : undefined,
      },
      {
        icon: 'clear_frame',
        text: __('Clear all objects'),
        eventPayload: {
          action: FrameContextMenuEvent.ClearFrame,
          frameId,
        },
        shouldShow: canManageFrame,
        isDisabled: isWhiteboardFrozen.value,
        rowStyle: isWhiteboardFrozen.value ? OzMenuRowsRowStyle.Disabled : undefined,
      },
    ].filter((item) => item.shouldShow !== false),
    [
      {
        icon: 'delete',
        text: __('Delete'),
        eventPayload: {
          action: FrameContextMenuEvent.DeleteFrame,
          frameId,
        },
        shouldShow: canManageFrame,
        isDisabled: isWhiteboardFrozen.value,
        rowStyle: isWhiteboardFrozen.value ? OzMenuRowsRowStyle.Disabled : OzMenuRowsRowStyle.Danger,
      },
    ].filter((item) => item.shouldShow !== false),
  ]

  // @ts-ignore: Type instantiation is excessively deep and possibly infinite. This is due to the use of recursive class VueCssClass in OzMenuRowsRow, we can safely ignore for now
  frameContextMenuGroups.value = menuGroups.filter((group) => !isEmpty(group))
}

const handleFrameContextMenuAction = (_: unknown, payload: FrameContextMenuEventPayload) => {
  const frameId = payload.frameId as TLPageId

  switch (payload.action) {
    case FrameContextMenuEvent.CopyLinkToFrame: {
      trackEvent('Whiteboard', 'Copied link to frame', null, null, { context: 'Context menu' })
      whiteboardStore.copyLinkToFrame(frameId)
      break
    }
    case FrameContextMenuEvent.CopyFrameBreakoutLink: {
      trackEvent('Whiteboard', 'Copied breakout link', null, null, { context: 'Context menu' })
      whiteboardStore.copyFrameBreakoutLink(frameId)
      break
    }
    case FrameContextMenuEvent.ChangeBackground: {
      trackEvent('Whiteboard', 'Opened change background panel', null, null, { context: 'Context menu' })
      whiteboardStore.panelFrameId = frameId
      if (device.app && isAppCapableOf('nativeWhiteboardBackgroundPicker')) {
        const selectedFrame = tldrawEditor?.getPage(frameId)
        if (!selectedFrame) break
        const selectedFrameMeta = selectedFrame.meta as PageMeta
        postMessage(
          openNativeWhiteboardBackgroundPicker({
            backgroundType: selectedFrameMeta.backgroundType ?? BackgroundType.Plain,
            customBackgroundUrl: sanitizeUrl(selectedFrameMeta.customBackgroundUrl as string),
            canIEditAllFrames: whiteboardPermissionsStore.canIEditAllFrames,
          }),
        )
        break
      }
      whiteboardStore.xFrameBackgroundPanel = true
      break
    }
    case FrameContextMenuEvent.ShareAndPermissions: {
      trackEvent('Whiteboard', 'Opened frame share and permissions panel', null, null, { context: 'Context menu' })
      whiteboardStore.panelFrameId = frameId
      whiteboardStore.xFrameSharePanel = true
      break
    }
    case FrameContextMenuEvent.MoveFrameUp: {
      trackEvent('Whiteboard', 'Moved frame up', null, null, { context: 'Context menu' })
      whiteboardStore.moveFrameUp(frameId)
      break
    }
    case FrameContextMenuEvent.MoveFrameDown: {
      trackEvent('Whiteboard', 'Moved frame down', null, null, { context: 'Context menu' })
      whiteboardStore.moveFrameDown(frameId)
      break
    }
    case FrameContextMenuEvent.AddFrameAbove: {
      trackEvent('Whiteboard', 'Added frame above', null, null, { context: 'Context menu' })
      whiteboardStore.addFrameAbove(frameId)
      break
    }
    case FrameContextMenuEvent.AddFrameBelow: {
      trackEvent('Whiteboard', 'Added frame below', null, null, { context: 'Context menu' })
      whiteboardStore.addFrameBelow(frameId)
      break
    }
    case FrameContextMenuEvent.Duplicate: {
      trackEvent('Whiteboard', 'Duplicated frame', null, null, { context: 'Context menu' })
      if (!whiteboardStore.getCanAddFrame()) {
        break
      }
      tldrawEditor?.batch(() => {
        const currentPage = tldrawEditor?.getCurrentPage()
        tldrawEditor?.duplicatePage(frameId)
        const newPage = tldrawEditor?.getCurrentPage()
        if (newPage) {
          // Editor.duplicatePage does not copy page meta by default
          tldrawEditor?.updatePage({ ...newPage, meta: currentPage?.meta })
        }
      })
      break
    }
    case FrameContextMenuEvent.ClearFrame: {
      trackEvent('Whiteboard', 'Cleared all objects', null, null, { context: 'Context menu' })
      const shapeIds = tldrawEditor?.getPageShapeIds(frameId) || []
      tldrawEditor?.markHistoryStoppingPoint('clear frame')
      tldrawEditor?.deleteShapes([...shapeIds])
      break
    }
    case FrameContextMenuEvent.DeleteFrame: {
      trackEvent('Whiteboard', 'Deleted frame', null, null, { context: 'Context menu' })
      whiteboardStore.deleteFrame(frameId)
      break
    }
    default:
      break
  }

  xFrameContextMenu.value = false
}

/*
 * PERMISSION
 */

const whiteboardPermissionsStore = useWhiteboardPermissionsStore()
const { isReadOnlyMode } = storeToRefs(whiteboardModeStore)

/*
 * TOOLBAR
 */

const xToolbar = computed(
  () =>
    whiteboardPermissionsStore.canIWriteFrame(whiteboardStore.currentFrameId as TLPageId) &&
    whiteboardModeStore.isDrawMode &&
    !isWhiteboardFrozen.value &&
    !whiteboardPanelsStore.xCardTemplatesPanel &&
    !whiteboardModeStore.isScreenshotMode,
)

/** UPLOAD PILLS */

const whiteboardUploadStore = useWhiteboardUploadStore()
const uploadPillRefs = ref<typeof WhiteboardUploadStatusPill[]>([])
function stopPeekingUploadPills() {
  uploadPillRefs.value.forEach((pill) => pill.stopPeeking())
}
function cancelStoppingPeekingUploadPills() {
  uploadPillRefs.value.forEach((pill) => pill.cancelStoppingPeeking())
}

function clearFinishedUploadPill(upload) {
  delete whiteboardUploadStore.uploads[upload.uploadKey]
  whiteboardUploadStore.uploads = { ...whiteboardUploadStore.uploads }
}

/** LINKING & LINKING PANEL */
const whiteboardLinkingStore = useWhiteboardLinkingStore()
function openLinkingPanel() {
  if (!tldrawEditor) return
  const shapeId = tldrawEditor.getOnlySelectedShapeId()
  if (!shapeId) return
  whiteboardLinkingStore.openLinkingPanel(shapeId)
}

/**
 * HIDING UNAPPROVED SHAPES AND INDICATORS IN INTERACT MODE
 */

const shapesHidingTemporarily = ref(new Set<TLShapeId>())
const appendedCss = ref<HTMLStyleElementWrapper | null>(null)

function hideAllUnapprovedShapesTemporarily(): void {
  const editor = getEditorObject()
  if (editor == null) {
    return
  }
  const shapes = editor
    .getPages()
    .flatMap((page) => Array.from(editor.getPageShapeIds(page.id)))
    .map((shapeId) => editor.getShape(shapeId)) as TLShape[]
  shapes.forEach((shape) => {
    if (!isShapeApproved(shape)) {
      document.getElementById(shape.id)?.classList.add('_tl-shape-hidden')
      shapesHidingTemporarily.value.add(shape.id)
    }
  })
}

function unhideAllUnapprovedShapes(): void {
  const editor = getEditorObject()
  if (editor == null) {
    return
  }
  shapesHidingTemporarily.value.forEach((shapeId) => {
    document.getElementById(shapeId)?.classList.remove('_tl-shape-hidden')
  })
  shapesHidingTemporarily.value.clear()
}

function hideAllUnapprovedIndicators(): void {
  appendedCss.value = appendCssToDocumentHead(`.unapproved-indicator { display: none !important; }`)
}

function unhideAllUnapprovedIndicators(): void {
  appendedCss?.value?.remove()
}

/**
 * PLANS
 */

const personalPlansStore = usePersonalPlansStore()

/**
 * POPOVER HEIGHT
 */

const { windowHeight } = storeToRefs(useWindowSizeStore())

const PADDING_FROM_EDGES = 12
const maxPopoverHeight = computed(() => windowHeight.value - PADDING_FROM_EDGES * 2)

const xSandboxAIChat = computed(() => {
  return isAppUsing('sandboxAi')
})
</script>

<template>
  <div
    id="wall-container"
    ref="wallContainerRef"
    :class="['flex', 'relative', 'overflow-hidden']"
    :style="{
      // box shadows don't render well in pdfs by default; this helps to fix that issue
      '-webkit-filter': whiteboardModeStore.isScreenshotMode ? 'blur(0)' : undefined,
    }"
    @pointerdown="checkAndDismissContextMenu"
  >
    <SurfaceWhiteboardHeader
      v-if="xHeader && !isHidingHeader"
      @background-pointerdown="handleClickingOutsideOfCanvas"
    />
    <SurfaceWhiteboardModeSwitcher
      v-if="
        (!surfaceContainerSizeStore.isContainerBiggerThanTabletLandscape ||
          (isTouchScreen() && surfaceContainerSizeStore.isLandscapeTablet)) &&
        !isHidingHeader &&
        !whiteboardModeStore.isScreenshotMode &&
        !surfaceStore.isWhiteboardEmbedPlayMode
      "
      :background-luminance="surfaceStore.backgroundLuminance"
      :class="['items-start', 'ms-14', 'mb-5']"
    />
    <div
      ref="whiteboardContainerRef"
      :class="[
        'w-full h-full flex items-center justify-center relative box-border',
        isAppUsing('sandboxCardTemplates') &&
          whiteboardCardTemplatesStore.isShowingCardTemplatePreview && [
            'transition-[padding-inline-start,padding-inline-end] motion-reduce:transition-none',
            {
              'duration-300': whiteboardPanelsStore.xCardTemplatesPanel,
              'duration-200': !whiteboardPanelsStore.xCardTemplatesPanel,
            },
          ],
        !isAppUsing('sandboxCardNavigation') &&
          (isLoadingPortraitMobileLayout || whiteboardModeStore.isCodeMode) &&
          'mb-4',
        !isLoadingPortraitMobileLayout && whiteboardModeStore.isDrawMode && 'mb-0 ',
        whiteboardModeStore.isCodeMode && 'items-stretch p-4 gap-4 box-border',
      ]"
      :style="{
        paddingInlineStart: `${paddingInlineStart}px`,
        paddingInlineEnd: `${paddingInlineEnd}px`,
      }"
      @pointerdown.self="handleClickingOutsideOfCanvas"
    >
      <!-- Frame bar -->

      <WhiteboardFrameBar
        v-if="xFrameBar"
        v-show="
          // When showing card templates panel, we only temporarily hide the frame bar.
          // Do not put this in v-if because we don't want to recalculate the frame bar position.
          !whiteboardPanelsStore.xCardTemplatesPanel
        "
        :container-top="containerTop"
        :container-bottom="containerBottom"
        :canvas-height="canvasHeight"
        :canvas-top="canvasTop"
        :toolbar-height="toolbarHeight"
        :toolbar-top="toolbarTop"
        :x-frame-context-menu="xFrameContextMenu"
        @show-frame-context-menu="buildFrameContextMenu"
        @pointerdown="unselectAllShapesAndSwitchToDefaultTool"
      />
      <template
        v-else-if="
          !hasError &&
          hasTldrawLoaded &&
          !whiteboardStore.isFrameBreakout &&
          !whiteboardModeStore.isFramePreviewMode &&
          !whiteboardModeStore.isFullWidthFramePreviewMode &&
          !whiteboardPanelsStore.xCardTemplatesPanel
        "
      >
        <WhiteboardFrameBarOld
          v-if="whiteboardModeStore.isDrawMode && !whiteboardModeStore.isScreenshotMode"
          :max-height="frameBarMaxHeight"
          :x-frame-context-menu="xFrameContextMenu"
          @show-frame-context-menu="buildFrameContextMenu"
        />
      </template>

      <WhiteboardFrameBreakoutNavigationBar v-if="xFrameBreakoutNavigationBar" />
      <!-- SCRIPT EDITOR -->
      <!-- NB: Since both code editor and whiteboard canvas are div, when code editor is mounted, we need the key so that the div that is rendering the canvas is not reused to
        render the code editor. If it is reused, the tldraw-app container will be unmounted and remounted, and that it will dispose of the canvas and the canvas will be blank. -->
      <div
        v-if="isScriptEditorEnabled"
        key="code-editor"
        :class="[
          surfaceStore.backgroundLuminance === 'dark' ? 'bg-dark-ui-100' : 'bg-light-ui-100',
          'border-box rounded-2.5xl overflow-hidden grow',
        ]"
      >
        <WhiteboardCodeEditor
          :selected-shape-id="selectedShapeIdForScript"
          :initial-script="initialScript"
          @save="saveScript"
          @cancel="xScriptEditor = false"
        />
      </div>

      <div
        key="whiteboard-canvas"
        :class="[
          'relative',
          whiteboardModeStore.isCodeMode ? 'flex flex-col box-border' : '',
          isAppUsing('sandboxCardTemplates') &&
            whiteboardCardTemplatesStore.isShowingCardTemplatePreview && [
              'transition-[width,height,inset-inline-start,top,transform] motion-reduce:transition-none',
              {
                'duration-300': whiteboardPanelsStore.xCardTemplatesPanel,
                'duration-200': !whiteboardPanelsStore.xCardTemplatesPanel,
              },
            ],
        ]"
        :style="{
          width: canvasWidth + 'px',
          height: whiteboardModeStore.isCodeMode ? '100%' : canvasHeight + 'px',
          insetInlineStart: insetInlineStart + 'px',
          top: whiteboardModeStore.isCodeMode ? '0' : insetTop + 'px',
          ...(shouldTransformCanvasPerspectiveAngle
            ? {
                transform:
                  dir() === 'rtl'
                    ? `perspective(${CANVAS_TEMPLATE_PREVIEW_PERSPECTIVE}px) rotateY(-${CANVAS_TEMPLATE_PREVIEW_ROTATE_Y_DEGREE}deg)`
                    : `perspective(${CANVAS_TEMPLATE_PREVIEW_PERSPECTIVE}px) rotateY(${CANVAS_TEMPLATE_PREVIEW_ROTATE_Y_DEGREE}deg)`,
                transformOrigin: dir() === 'rtl' ? 'left center' : 'right center',
              }
            : {}),
        }"
      >
        <TransitionGroup
          v-if="
            !shouldEnableWhiteboardInPlaceUploadingAnimation() &&
            !whiteboardModeStore.isPlayMode &&
            !whiteboardModeStore.isScreenshotMode
          "
          tag="div"
          leave-active-class="transition-opacity motion-reduce:transition-none"
          leave-class="opacity-100"
          leave-to-class="opacity-0"
          :class="['absolute', 'end-0', 'flex', 'gap-2', 'flex-wrap-reverse', 'justify-end', 'overflow-hidden']"
          :style="{
            bottom: canvasHeight + 4 + 'px',
          }"
          @pointerenter="cancelStoppingPeekingUploadPills"
          @pointerleave="stopPeekingUploadPills"
        >
          <WhiteboardUploadStatusPill
            v-for="upload in whiteboardUploadStore.uploads"
            ref="uploadPillRefs"
            :key="upload.uploadKey"
            :progress="upload.progress"
            :done="upload.progress === null"
            :file-name="upload.fileName"
            :type="upload.type"
            :canvas-width="canvasWidth"
            @clear="clearFinishedUploadPill(upload)"
            @cancel="whiteboardUploadStore.cancelUploadOld(upload.uploadKey)"
          />
        </TransitionGroup>
        <!-- NB: we have to force ltr for canvas to apply transform correctly -->
        <div
          dir="ltr"
          :class="[
            'overflow-hidden relative',
            !whiteboardModeStore.isFullWidthFramePreviewMode && 'rounded-xl',
            whiteboardModeStore.isCodeMode ? 'shrink-0' : '',
            isAppUsing('sandboxCardTemplates') &&
              whiteboardCardTemplatesStore.isShowingCardTemplatePreview && [
                'transition-[width,height] motion-reduce:transition-none',
                {
                  'duration-300': whiteboardPanelsStore.xCardTemplatesPanel,
                  'duration-200': !whiteboardPanelsStore.xCardTemplatesPanel,
                },
              ],
          ]"
          :style="{ width: canvasWidth + 'px', height: canvasHeight + 'px' }"
        >
          <SurfaceWhiteboardCanvasDragAndDropOverlay
            v-if="shouldEnableWhiteboardInPlaceUploadingAnimation() && isDragging"
          />
          <!-- Clicking on the tldraw canvas doesn't trigger pointerdown on the wallContainer, so we need to add it here too -->
          <div
            id="tldraw-app"
            ref="tldrawAppRef"
            v-emit-long-press="defaultTldrawOptions.longPressDurationMs"
            :class="[
              'w-full h-full',
              {
                'transparent-background': isTransparentBackground,
              },
            ]"
            @long-press.prevent="whiteboardElementModifierStore.buildContextMenu"
            @contextmenu.prevent="whiteboardElementModifierStore.buildContextMenu"
            @pointerdown="handleTldrawAppPointerDown"
          />
        </div>
        <WhiteboardCodeFrameObjectPicker
          v-if="!hasError && hasTldrawLoaded && whiteboardModeStore.isCodeMode"
          class="flex-1"
          :selected-shape-id-for-script="selectedShapeIdForScript"
          @switch-frame="switchFrame"
        />
        <WhiteboardStartFromTemplateButton v-if="whiteboardCardTemplatesStore.canShowStartFromATemplateButton" />
      </div>

      <template v-if="hasTldrawLoaded">
        <WhiteboardElementContextMenu
          v-if="xContextMenu && contextMenuAnchor"
          ref="contextMenuRef"
          :boundary="wallContainerRef"
          :max-height="maxPopoverHeight"
        />
        <WhiteboardAttachmentPicker v-if="whiteboardModeStore.isDrawMode" />

        <WhiteboardToolbar v-if="xToolbar && !hasError" ref="toolbar" @pointerdown="handleTldrawAppPointerDown" />
        <WhiteboardAiChatContainer v-if="xSandboxAIChat" class="absolute bottom-0 end-0" />
        <!-- Secondary menu(s) -->
        <!-- Place it at top level so that it can render below the toolbar -->
        <template
          v-if="
            whiteboardToolbarStore.xSecondaryMenu &&
            whiteboardStore.selectedToolbarButtonEl &&
            whiteboardStore.selectedTool &&
            whiteboardStore.selectedTool.secondaryMenu
          "
        >
          <!-- Secondary Menu (color or geo shape) -->
          <!-- Geo shape or color Menu -->
          <Transition
            enter-active-class="transform-gpu transition-[opacity,transform] duration-250 ease-out motion-reduce:transition-none"
            leave-active-class="transform-gpu transition-[opacity,transform] duration-150 ease-in motion-reduce:transition-none"
            :enter-class="
              !isLoadingPortraitMobileLayout
                ? 'opacity-0 translate-s-full'
                : 'opacity-0 translate-y-full rtl:-translate-y-full'
            "
            :leave-to-class="
              !isLoadingPortraitMobileLayout
                ? 'opacity-0 translate-s-full'
                : 'opacity-0 translate-y-full rtl:-translate-y-full'
            "
            appear
          >
            <OzBasePopover
              :key="whiteboardStore.selectedTool.id"
              class="z-modal"
              :anchor="whiteboardStore.selectedToolbarButtonEl"
              :config="secondaryMenuPopoverConfig"
              :color="OzBasePopoverColors.None"
            >
              <WhiteboardSecondaryMenu
                :mode="!isLoadingPortraitMobileLayout ? 'toolbar' : 'toolbarHorizontal'"
                :color-scheme="surfaceStore.backgroundLuminance"
                :secondary-menus="whiteboardToolbarStore.currentSecondaryMenus"
                :secondary-selection-ids="whiteboardToolbarStore.currentSecondarySelectionIds"
                :icon-color="
                  whiteboardStore.selectedToolId === 'geo' ? whiteboardStore.geoColorMenuSelectionId : undefined
                "
                :icons-per-line="
                  whiteboardStore.selectedToolId === 'geo' || whiteboardStore.selectedToolId === 'arrow' ? 3 : 1
                "
                @select="whiteboardToolbarStore.handleSecondaryMenuSelection"
              />
            </OzBasePopover>
          </Transition>
        </template>

        <WhiteboardElementModifierContainer v-if="whiteboardElementModifierStore.selectedShapes.length > 0" />
      </template>
    </div>

    <!-- PLAY MODE NAVIGATION BAR -->
    <WhiteboardPlayModeNavigationBar
      v-if="whiteboardModeStore.isPlayMode && !whiteboardModeStore.isScreenshotMode && !whiteboardStore.isFrameBreakout"
      ref="whiteboardPlayModeNavigationBarRef"
    />
    <!-- FRAME CONTEXT MENU -->
    <!-- Render here instead of inside the frame bar component because we want the scrim to apply to the entire page, not just the frame bar -->
    <OzPopover
      v-if="xFrameContextMenu && frameContextMenuAnchor"
      :anchor="frameContextMenuAnchor"
      class="z-modal"
      :config="{
        middleware: [
          offset(4),
          flip({
            fallbackPlacements: [
              'right-start',
              'right',
              'right-end',
              'left-start',
              'left',
              'left-end',
              'bottom',
              'bottom-end',
              'bottom-start',
              'top',
              'top-end',
              'top-start',
            ],
            boundary: wallContainerRef,
          }),
          shiftUpWhenOverflow(),
        ],
      }"
      :color="OzBoxColors.PrimaryBlur"
      :dark-mode="surfaceStore.backgroundLuminance === 'dark'"
      :x-scrim="false"
      @scrim-click="xFrameContextMenu = false"
      @scrim-esc="xFrameContextMenu = false"
    >
      <div
        :class="[
          'grid grid-flow-row grid-cols-1',
          'gap-0',
          'overflow-y-auto',
          'rounded-2.5xl',
          'w-[240px]',
          surfaceStore.backgroundLuminance === 'dark' ? 'scrollbar-thin-dark' : 'scrollbar-thin',
        ]"
        :style="{ maxHeight: `${maxPopoverHeight}px` }"
        role="menu"
      >
        <div
          v-for="(actionList, i) in frameContextMenuGroups"
          :key="i"
          :class="[
            'tablet-portrait:rounded-none',
            'bg-transparent',
            surfaceStore.backgroundLuminance === 'dark' ? 'bg-grey-800' : 'bg-grey-50',
          ]"
        >
          <OzMenuRows
            :size-preset="OzMenuRowsSizePreset.H44px"
            :dark-mode="surfaceStore.backgroundLuminance === 'dark'"
            :focus-style="OzMenuRowsFocusStyle.Bubble200"
            :rows="actionList"
            :class="{
              'pt-1': i === 0,
              'pb-1': i === frameContextMenuGroups.length - 1,
            }"
            :accessibility-role="'group'"
            @select-row="handleFrameContextMenuAction"
          />
          <OzDivider
            v-if="i < frameContextMenuGroups.length - 1"
            :dark-mode="surfaceStore.backgroundLuminance === 'dark'"
            :color-scheme="OzDividerColorScheme.Popover"
            :thickness="8"
          />
        </div>
      </div>
    </OzPopover>

    <!-- PANELS -->

    <Portal selector="#surface-container">
      <template v-if="whiteboardStore.xFrameBackgroundPanel">
        <WhiteboardFrameBackgroundPanel v-if="isAppUsing('sandboxThemeBackgrounds')" />
        <WhiteboardFrameBackgroundPanelOld v-else />
      </template>
      <WhiteboardFrameSharePanel v-if="whiteboardStore.xFrameSharePanel" />
      <WhiteboardFrameShareViaAnotherAppPanel v-if="whiteboardStore.xFrameShareViaAnotherAppPanel" />
      <WhiteboardFrameBreakoutLinksPanel v-if="whiteboardStore.xFrameBreakoutLinksPanel" />
      <WhiteboardLinkingPanel v-if="whiteboardLinkingStore.xLinkingPanel" />

      <MembershipTierUpgradeModal
        v-if="personalPlansStore.xUpgradeModal"
        :user="surfaceStore.user"
        :whiteboard-frames-used="whiteboardStore.frames.length"
        :file-that-exceeded-size="whiteboardUploadStore.fileThatExceededSize"
        @cancel="personalPlansStore.closePlanUpgradeModal"
      />
    </Portal>

    <!-- WHITEBOARD EXPORT -->
    <!-- Banksy looks for WHITEBOARD_ATTACHMENTS_LOADED_ID to determine when the whiteboard is ready for export. -->
    <!-- We mark the whiteboard as ready for export as soon as all attachments have either been loaded, or have errored. -->
    <!-- We don't want errored attachments to block the whiteboard export. -->
    <!-- NOTE: We also need to wait for Tldraw app to render first. -->
    <div
      v-if="hasTldrawLoaded && whiteboardAttachmentsStore.hasLoadedOrErroredAllAttachments"
      :id="WHITEBOARD_ATTACHMENTS_LOADED_ID"
      data-testid="whiteboardContentLoaded"
      :aria-hidden="true"
    />
  </div>
</template>

<style lang="postcss">
@import url('tldraw-root/tldraw.css');
.tl-text {
  text-shadow: none !important;
}

/* Shantell Sans */
.tl-text-wrapper[data-font='draw'] .tl-text {
  font-feature-settings: 'calt', 'ss01', 'ss02', 'ss03', 'ss04', 'ss05', 'ss06', 'ss07', 'ss08', 'ss09', 'liga';
}

/* Inter */
.tl-text-wrapper[data-font='sans'] .tl-text {
  font-feature-settings: 'ss01', 'cv08', 'calt';
}

:root {
  .tl-theme__light {
    --color-selection-stroke: #9466e8 !important;
    --color-selection-fill: rgba(148, 102, 232, 0.24) !important;
    --color-selected: #9466e8 !important;
  }
  --safe-area-inset-top: env(safe-area-inset-top);
  --safe-area-inset-right: env(safe-area-inset-right);
  --safe-area-inset-bottom: env(safe-area-inset-bottom);
  --safe-area-inset-left: env(safe-area-inset-left);
}

/* Override style from _textfield.scss */
textarea {
  box-shadow: none !important;
}

/* Override tldraw default background-color */
#tldraw-app.transparent-background .tl-background {
  background-color: transparent !important;
}

#tldraw-app .tl-shape:has(._tl-shape-hidden) {
  display: none !important;
}

.tl-corner-handle {
  rx: 50%;
}

/* 
Apply a default font-weight of 500 to all fonts except Courier Prime (mono font).
The tl-rich-text class is included because tldraw uses it internally to measure text dimensions.
See renderHtmlFromRichTextForMeasurement() which wraps HTML in this class.
@see https://github.com/tldraw/tldraw/blob/3bf31007c5a7274f3f7926a84c96c89a4cc2c278/packages/tldraw/src/lib/utils/text/richText.ts#L87C17-L87C53
*/
.tl-text-wrapper[data-font='sans'],
.tl-text-wrapper[data-font='draw'],
.tl-text-wrapper[data-font='serif'],
.tl-rich-text {
  font-weight: 500; /* goodcheck-disable-line */
}
.tl-text-wrapper[data-font='mono'] {
  font-weight: 400;
}

.tl-text-wrapper,
.tl-rich-text {
  ul {
    list-style-type: disc;
    padding-left: 2ch;
  }

  ol {
    list-style-type: decimal;
  }

  strong {
    font-weight: 700; /* goodcheck-disable-line */
  }
}
</style>
